import { useMsal } from "@azure/msal-react";
import { FocusTrapZone, Icon, Layer, Overlay, Stack, keyframes, mergeStyles } from "@fluentui/react";
import { RootState } from "app/store";
import { saveAs } from "file-saver";
import {
    TCategoryEntity as FAM_TCategoryEntity
} from "model/FlexibleAttributeModel";
import { IGLWindow } from "model/GroupLibraryWindow";
import { useEffect, useRef, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router";
import { AdvancedTerminal } from "./AdvancedTerminal";
import { crmEnvironments } from "./Application/Constants";
import { AppLinkTo, EViewType, emitCustomEvent, slideRightIn400 } from "./Application";
import { useApplication } from "./Application/Context";
import { loadSnapshotAction } from "./Application/dataActions";
import { useAuthentication } from "./Authentication";
import { updateSnapshotData } from "./Reducer/dataSlice";
import { setProcessing, setViewType, updateDebug } from './Reducer/statusSlice';
import { EJsonTransformerRuntime, JsonTransformer } from "./Transform/JsonTransformer";
import { RemoveDeprecatedFields } from "./Transform/RemoveDeprecatedFields";

declare global {
    interface Array<T> {
        insert(index: number, elem: T[]): void;
        remove(elem: T): Array<T>;
    }
}


export interface IDebugOptions {
    admin?: boolean;
    diagnosis?: boolean;
    exception?: boolean;
    clone?: boolean;
    disableAutosave?: boolean;
    autoSaveOff?: boolean;
    ignoreVersion?: boolean;
    liveload?: boolean;
    noVisibility?: boolean;
    checklistForceRecycle?: boolean;
    developer?: boolean;
    quickLoad?: boolean;
    preview?: boolean;
};



const consoleFadeIn = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});

const terminalClass = mergeStyles({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    padding: 'clamp(20px, 4vw, 48px)',
    background: 'radial-gradient(ellipse at top left, #172337 0%, #0b1019 48%, #070a10 100%)',
    color: '#e7edf7',
    fontFamily: 'Segoe UI, sans-serif',
    overflow: 'hidden',
    animationName: consoleFadeIn,
    animationDuration: '1200ms',
    animationTimingFunction: 'ease-in-out',
    animationFillMode: 'both',
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
});

const terminalHeaderClass = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '1040px',
    flexShrink: 0,
    margin: '0 auto 20px',
    paddingBottom: '14px',
    borderBottom: '1px solid #263348',
});

const terminalBodyClass = mergeStyles({
    display: 'grid',
    flex: '1 1 auto',
    minHeight: 0,
    gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)',
    gap: '20px',
    width: '100%',
    maxWidth: '1040px',
    margin: '0 auto',
    overflow: 'hidden',
    '@media (max-width: 640px)': {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto minmax(0, 1fr)',
        gap: '12px',
    },
});

const navigationClass = mergeStyles({
    alignSelf: 'start',
    minHeight: 0,
    maxHeight: '100%',
    padding: '10px',
    border: '1px solid #263348',
    borderRadius: '12px',
    background: 'rgba(15, 23, 36, 0.88)',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    '::-webkit-scrollbar': { width: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: '#33435a', borderRadius: '8px' },
    '@media (max-width: 640px)': {
        maxHeight: '24vh',
    },
});

const contentClass = mergeStyles({
    minHeight: 0,
    maxHeight: '100%',
    padding: '24px',
    border: '1px solid #263348',
    borderRadius: '12px',
    background: 'rgba(15, 23, 36, 0.88)',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    '::-webkit-scrollbar': { width: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: '#33435a', borderRadius: '8px' },
    '@media (max-width: 640px)': {
        padding: '16px',
    },
});

const submenuClass = mergeStyles({
    marginBottom: '12px',
    border: '1px solid #263348',
    borderRadius: '9px',
    background: 'rgba(7, 11, 18, 0.35)',
    overflow: 'hidden',
});

const submenuSummaryClass = mergeStyles({
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    color: '#d7e0ed',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    listStyle: 'none',
    ':hover': { background: '#1c293b' },
    '::-webkit-details-marker': { display: 'none' },
});

const expandCollapseIconClass = mergeStyles({
    flexShrink: 0,
    color: '#8fa2bd',
    fontSize: '12px',
    transition: 'transform 0.15s ease',
    selectors: {
        'details:not([open]) &': {
            transform: 'rotate(-90deg)',
        },
    },
});

const submenuContentClass = mergeStyles({
    padding: '4px 8px 8px',
    borderTop: '1px solid #263348',
});

const anchorClass = mergeStyles({
    boxSizing: 'border-box',
    width: '100%',
    padding: '11px 12px',
    textDecoration: 'none',
    textAlign: 'left',
    fontSize: '14px',
    lineHeight: '1.4',
    color: '#c5d0e0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'background 0.15s ease, color 0.15s ease',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    ':hover': {
        color: '#fff',
        background: '#202d41',
    }
});

const actionIconClass = mergeStyles({
    flexShrink: 0,
    width: '16px',
    color: '#9bb0cb',
    fontSize: '15px',
});

const getToggleIcon = (enabled?: boolean) => (
    <Icon
        className={actionIconClass}
        iconName={enabled ? 'CircleFill' : 'CircleRing'}
        styles={{
            root: {
                color: enabled ? '#68b88a' : '#9bb0cb',
                fontSize: '12px',
                textShadow: enabled ? '0 0 6px rgba(104, 184, 138, 0.65), 0 0 14px rgba(104, 184, 138, 0.3)' : 'none',
            },
        }}
        aria-hidden="true"
    />
);

export interface IDebuggerProps extends TComponentConnectedProps {

}

enum IDebuggerState {
    unknown,
    administrative,
    developer,
    advanced,
}

const advancedCommands = ['migration', 'remove-deprecated', 'remove-duplicates'] as const;
type AdvancedCommand = typeof advancedCommands[number];

const Debugger: React.FC<IDebuggerProps> = (props) => {

    const [hidden, setHidden] = useState(true);
    const authentication = useAuthentication();
    const dispatch = useDispatch();
    const { instance } = useMsal();

    const state = useSelector((state: RootState) => state);

    const [viewModel, setViewModel] = useState(
        {
            mode: IDebuggerState.administrative
        }
    );
    const [terminalLines, setTerminalLines] = useState<string[]>(['Type help to list commands.']);
    const [terminalBusy, setTerminalBusy] = useState(false);
    const terminalRunning = useRef(false);

    const history = useHistory();

    const application = useApplication();
    const dataSource = application.dataSource;

    useEffect(() => {

        const debuggerOnKeyUp = (event: KeyboardEvent) => {
            if (event.code === 'Backquote' && !event.altKey) {
                setHidden(t => !t);

            } else if (event.code === 'Escape') {
                setHidden(true)
            }
        }

        document.addEventListener('keyup', debuggerOnKeyUp);

        return () => {
            document.removeEventListener('keyup', debuggerOnKeyUp)
        }
    }, []);

    const debugRemoveDeprecatedFields = async () => {

        const snapshot = state.data.snapshot;
        const jsonNew = structuredClone(state.data.json);

        const transformer = new JsonTransformer(
            [
                new RemoveDeprecatedFields({
                    config: state.data.config,
                    json: jsonNew
                })
            ],
            EJsonTransformerRuntime.ESequential
        );

        await transformer.transformAsync(jsonNew, snapshot);

        dispatch(updateDebug({ autoSaveOff: true }));
        dispatch(updateSnapshotData({ json: jsonNew }));

    }

    const debugRemoveDuplicates = async () => {
        const snapshot = state.data.snapshot;
        const jsonNew = structuredClone(state.data.json);

        [
            ...jsonNew.benefits,
            ...jsonNew.feesAndPremiums,
            ...jsonNew.plans,
            ...jsonNew.claimsARs,
            ...jsonNew.stopLoss,
        ].forEach((eachEntity, ix) => {
            const attributeSetByIds = {} as Record<string, FAM_TCategoryEntity[]>;

            eachEntity.attributeSets.forEach((eachAttributeSet, iy) => {
                const t = attributeSetByIds[eachAttributeSet.id] ?? [];
                attributeSetByIds[eachAttributeSet.id] = t;
                t.push(eachAttributeSet);
            });

            Object.keys(attributeSetByIds)
                .forEach((k) => {

                    const duplicates = attributeSetByIds[k];

                    if (duplicates.length > 1) {

                        // if (testEntity[0].id === '38e85f18-84a2-4f80-a3c3-8805a5856ef5') {
                        //     console.log('debug');
                        //     debugger;
                        // }

                        if (duplicates[0].id === 'c4bc5bcb-0e75-45a6-8a1d-5f01eb9c9382') {
                            console.log('debug');
                            debugger;
                        }

                        // are they all the same?
                        let valid = true;

                        const toRemoves: typeof duplicates[0][] = [];
                        let toKeep: typeof toRemoves[0] | null = null

                        duplicates.forEach((t, index) => {
                            if (toKeep === null && t.deactivated !== true) {
                                toKeep = t;
                            } else {
                                toRemoves.push(t);
                            }
                        });

                        const ignoreIds = ['']; // temp ignore for known differences

                        {
                            const first = JSON.stringify(duplicates[0]);
                            toRemoves.forEach((t) => {
                                if (ignoreIds.indexOf(t.id ?? '') >= 0)
                                    return;
                                const compare = JSON.stringify(t);
                                if (compare !== first) {
                                    console.log(`first/compare`, first, compare);
                                    valid = false;
                                }
                            });

                        }

                        valid = true;
                        if (!valid)
                            throw new Error(`Cannot remove duplicate attribute set ${k} as they are not identical`);

                        // remove duplicates
                        debugger;
                        toRemoves.forEach((t) => {
                            const indexof = eachEntity.attributeSets.findIndex(q => q === t);
                            eachEntity.attributeSets.splice(indexof, 1);
                            console.log(`Removed duplicate attribute set ${k}`, t);
                        });
                    }

                })

        });

        dispatch(updateDebug({ autoSaveOff: true }));
        dispatch(updateSnapshotData({ json: jsonNew }));
    }

    const onRenderScreenWrap = (child: JSX.Element) => {
        if (hidden)
            return <></>;

        return <Layer eventBubblingEnabled>
            <Overlay styles={{ root: { background: '#070a10' } }}>
            </Overlay>
            <FocusTrapZone style={{ position: 'absolute', height: '100%', width: '100%' }} forceFocusInsideTrap={true}>
                <Stack styles={{ root: { height: '100%', width: '100%' } }}>
                    <Stack.Item styles={{ root: { height: '100%', width: '100%' } }}>
                        {child}
                    </Stack.Item>
                </Stack>
            </FocusTrapZone>
        </Layer>
    }

    const onRenderDeveloper = (showNavigation = true) => {

        const isActive = viewModel.mode === IDebuggerState.developer;
        const view = <button
            className={anchorClass}
            title="Open data management tools for downloads and version settings."
            onClick={() => {
                setViewModel(vm => ({ ...vm, mode: isActive ? IDebuggerState.unknown : IDebuggerState.developer }))
            }}
            aria-expanded={isActive}
            aria-controls="debugger-developer-options"
            style={isActive ? { color: '#fff', background: '#263953' } : undefined}
        >
            <Icon className={actionIconClass} iconName="Code" aria-hidden="true" />
            <span style={{ textTransform: 'uppercase' }}>Data Management</span>
            <Icon iconName={isActive ? 'ChevronDown' : 'ChevronRight'} styles={{ root: { marginLeft: 'auto', color: '#8fa2bd', fontSize: '12px' } }} aria-hidden="true" />
        </button>

        if (!showNavigation)
            return view;

        if (!isActive)
            return <></>;

        return <div id="debugger-developer-options" className={slideRightIn400}>
            <input type="file" id="browse" name="fileupload" style={{ display: 'none' }} />
            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Download current or stored data, export configuration, or replace the current JSON data.">Snapshots &amp; data <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    <button
                        className={anchorClass}
                        title="Download the current in-memory data as JSON, including changes not yet saved."
                        onClick={() => {

                            if (state.data.opportunityProduct) {
                                const log = JSON.stringify(state.data.opportunityProduct);
                                const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                                saveAs(blob, `${state.data.snapshot.bcbs_name}.json`, { autoBom: true });

                            } else {
                                const log = JSON.stringify(state.data.json);
                                const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                                saveAs(blob, `${state.data.snapshot.bcbs_name}.json`, { autoBom: true });
                            }

                        }} >
                        <Icon className={actionIconClass} iconName="Database" aria-hidden="true" /> Download current data (JSON)
                    </button>

                    <button
                        className={anchorClass}
                        title="Enter a snapshot ID to download its stored JSON data."
                        onClick={async () => {
                            const result = prompt('Enter the snapshot ID.', props?.state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid);

                            if (!result)
                                return;

                            const snapshot = await dataSource.getGroupConfigurationSnapshot(result);

                            if (snapshot) {
                                let result = await dataSource.downloadGroupConfigurationSnapshot(snapshot);

                                if (result?.json) {
                                    const jsonStringify = JSON.stringify(result?.json);
                                    const blob = new Blob([jsonStringify], { type: 'text/plan;charset=utf-8' });
                                    saveAs(blob, `${snapshot.bcbs_name}.json`, { autoBom: true });
                                }
                            }
                        }}>
                        <Icon className={actionIconClass} iconName="Download" aria-hidden="true" /> Download stored snapshot (JSON)
                    </button>

                    <button
                        className={anchorClass}
                        title="Download the currently loaded release configuration as JSON."
                        onClick={() => {
                            const log = JSON.stringify(state.data.config);
                            const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                            saveAs(blob, 'config.json', { autoBom: true });
                        }} >
                        <Icon className={actionIconClass} iconName="Download" aria-hidden="true" /> Download configuration (JSON)
                    </button>

                    <button
                        className={anchorClass}
                        title="Choose a JSON file to replace the current snapshot data in memory. This does not merge the data."
                        onClick={() => {
                            const fileInput = document.getElementById("browse")! as HTMLInputElement;
                            const handler = (event: any) => {
                                fileInput.removeEventListener('change', handler)
                                // add first image, if available
                                if (fileInput.files?.length) {
                                    const x = fileInput.files[0];
                                    const reader = new FileReader();
                                    reader.onload = function (e: any) {
                                        const json = reader.result as string;
                                        const data = JSON.parse(json);
                                        dispatch(updateSnapshotData({ ...state.data, json: data }));
                                    }
                                    reader.readAsText(x);
                                };

                            }

                            fileInput.addEventListener('change', handler);
                            fileInput.click();
                        }} >
                        <Icon className={actionIconClass} iconName="Refresh" aria-hidden="true" /> Replace current data from JSON
                    </button>
                    <button className={anchorClass} title="Generate and download the data dictionary extract." onClick={
                        () => {
                            (async () => {
                                try {
                                    dispatch(setProcessing(true));
                                    const response = await dataSource.getGenerateDataDictionaryExtract();
                                    saveAs(response.blob, response.fileName);
                                } catch (error) {
                                    emitCustomEvent({
                                        eventType: 'globalException',
                                        details: error
                                    });
                                } finally {
                                    dispatch(setProcessing(false));
                                }
                            })();
                        }} >
                        <Icon className={actionIconClass} iconName="Download" aria-hidden="true" /> Download data model
                    </button>
                </div>
            </details>

            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Open the snapshot timeline or change version-check behavior.">Navigation &amp; versioning <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    {authentication.authorizations?.roles["BCBS - Power User"] &&
                        <button
                            className={anchorClass}
                            title="Navigate to the snapshot timeline."
                            onClick={() => { history.push(AppLinkTo(props.state, { type: 'timeline', id: '' })) }} >
                            <Icon className={actionIconClass} iconName="Clock" aria-hidden="true" /> Open timeline
                        </button>
                    }

                    <button
                        className={anchorClass}
                        title="Toggle the debug option that bypasses version checks."
                        onClick={
                            () => {
                                dispatch(updateDebug({ ignoreVersion: !state.status.debug.ignoreVersion }));
                            }} >
                        {getToggleIcon(state.status.debug.ignoreVersion)} Ignore version checks
                    </button>

                </div>
            </details>
        </div>
    }

    const onRenderAdministrative = (showNavigation = true) => {
        const isActive = viewModel.mode === IDebuggerState.administrative;
        const view = <button
            className={anchorClass}
            title="Open environment links, configuration reload, access, and diagnostic settings."
            style={isActive ? { color: '#fff', background: '#263953' } : undefined}
            aria-expanded={isActive}
            aria-controls="debugger-administrative-options"
            onClick={() => { setViewModel(vm => ({ ...vm, mode: isActive ? IDebuggerState.unknown : IDebuggerState.administrative })) }}
        >
            <Icon className={actionIconClass} iconName="Settings" aria-hidden="true" />
            <span style={{ textTransform: 'uppercase' }}>Administration</span>
            <Icon iconName={isActive ? 'ChevronDown' : 'ChevronRight'} styles={{ root: { marginLeft: 'auto', color: '#8fa2bd', fontSize: '12px' } }} aria-hidden="true" />
        </button>

        if (!showNavigation)
            return view;

        if (!isActive)
            return <></>;

        return <div id="debugger-administrative-options" className={slideRightIn400}>
            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Reload with alternate configuration or visibility settings, or toggle preview mode. Configuration and visibility reloads disable autosave.">Reload options <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    <button
                        className={anchorClass}
                        title="Reload the current snapshot with live configuration and disable autosave."
                        onClick={() => {
                            dispatch(updateDebug({ liveload: true }));
                            dispatch(updateDebug({ autoSaveOff: true }));
                            dispatch(loadSnapshotAction(
                                dataSource,
                                state.data.account?.accountid!,
                                {
                                    primarySnapshotId: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                                }
                            ));
                        }}
                    >
                        {getToggleIcon(state.status.debug.liveload)} Reload with live configuration
                    </button>


                    <button
                        className={anchorClass}
                        title="Reload the current snapshot without visibility transformers and disable autosave."
                        onClick={() => {
                            dispatch(updateDebug({ noVisibility: true }));
                            dispatch(updateDebug({ autoSaveOff: true }));
                            dispatch(loadSnapshotAction(
                                dataSource,
                                state.data.account?.accountid!,
                                {
                                    primarySnapshotId: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                                }
                            ));
                        }}
                    >
                        {getToggleIcon(state.status.debug.noVisibility)} Reload without visibility transformers
                    </button>
                    <button className={anchorClass} title="Toggle preview mode and reload the page. Save any changes before continuing." onClick={
                        () => {
                            if (document.cookie.split(';').some(q => q.indexOf('preview=yes') > -1)) {
                                document.cookie = "preview=;path=/";
                                (window as IGLWindow).preview = false;
                            } else {
                                document.cookie = "preview=yes;path=/";
                                (window as IGLWindow).preview = true;
                            }
                            dispatch(setProcessing(true));
                            window.location.reload();
                        }
                    } >
                        {getToggleIcon((window as IGLWindow).preview)} Reload with Preview mode
                    </button>
                </div>
            </details>

            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Change the view mode or enable debug access and diagnostic settings.">Access &amp; diagnostics <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    <button className={anchorClass} title="Switch between edit and view-only modes. Autosave is disabled when switching." onClick={
                        () => {
                            dispatch(updateDebug({ autoSaveOff: true }));

                            if (state.status.viewType === EViewType.ViewOnly)
                                dispatch(setViewType(EViewType.Modify));
                            else
                                dispatch(setViewType(EViewType.ViewOnly));

                        }} >
                        {state.status.viewType === EViewType.ViewOnly && <><Icon className={actionIconClass} iconName="Edit" aria-hidden="true" /> Switch to edit mode</>}
                        {state.status.viewType === EViewType.Modify && <><Icon className={actionIconClass} iconName="View" aria-hidden="true" /> Switch to view-only mode</>}
                        {state.status.viewType !== EViewType.ViewOnly && state.status.viewType !== EViewType.Modify && <><Icon className={actionIconClass} iconName="Help" aria-hidden="true" /> View mode unavailable</>}
                    </button>

                    <button className={anchorClass} title="Toggle diagnostic mode and disable autosave." onClick={
                        () => {
                            dispatch(updateDebug({ diagnosis: !state.status.debug.diagnosis }));
                            dispatch(updateDebug({ autoSaveOff: true }));
                        }} >
                        {getToggleIcon(state.status.debug.diagnosis)} Diagnostic mode
                    </button>

                    <button className={anchorClass} title="Toggle the administrator debug mode and disable autosave. This does not grant account permissions." onClick={
                        () => {
                            dispatch(updateDebug({ admin: !state.status.debug.admin }));
                            dispatch(updateDebug({ autoSaveOff: true }));
                        }} >
                        {getToggleIcon(state.status.debug.admin)} Administrator mode
                    </button>
                </div>
            </details>

            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Open the local portal or the current snapshot's Dynamics record in another environment.">Environment <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    <button
                        className={anchorClass}
                        title="Open the current portal page on localhost:44350."
                        onClick={() => {
                            const target = new URL(window.location.href);
                            target.protocol = 'https:';
                            target.host = 'localhost:44350';
                            window.open(target.toString(), '_blank', 'noopener,noreferrer');
                        }}
                    >
                        <Icon className={actionIconClass} iconName="Globe" aria-hidden="true" /> Open in Localhost
                    </button>
                    {[
                        { label: 'Open in BCBSOnlineDev', ...crmEnvironments.Development },
                        { label: 'Open in BCBSOnlineSys', ...crmEnvironments.System },
                        { label: 'Open in BCBSOnline', ...crmEnvironments.Production },
                    ].map(environment => (
                        <button
                            key={environment.baseUrl}
                            className={anchorClass}
                            disabled={!state.data.snapshot?.bcbs_groupconfigurationsnapshotid}
                            title={`Open the current snapshot's Dynamics record at ${environment.baseUrl}.`}
                            onClick={() => {
                                const snapshotId = state.data.snapshot?.bcbs_groupconfigurationsnapshotid;
                                if (!snapshotId) return;

                                const target = new URL('/main.aspx', environment.baseUrl);
                                target.search = new URLSearchParams({
                                    appid: environment.appId,
                                    forceUCI: '1',
                                    pagetype: 'entityrecord',
                                    etn: 'bcbs_groupconfigurationsnapshot',
                                    id: snapshotId,
                                }).toString();
                                window.open(target.toString(), '_blank', 'noopener,noreferrer');
                            }}
                        >
                            <Icon className={actionIconClass} iconName="Globe" aria-hidden="true" /> {environment.label}
                        </button>
                    ))}
                </div>
            </details>
        </div >
    }

    const appendTerminalLine = (line: string) => {
        setTerminalLines(lines => [...lines, line].slice(-100));
    };

    const runAdvancedCommand = async (command: AdvancedCommand) => {
        if (terminalRunning.current) {
            appendTerminalLine('Another command is running. Wait for it to finish.');
            return;
        }
        if (!window.confirm(`Run "${command}"? This command can modify data.${command === 'migration' ? ' The migration may update multiple snapshots.' : ''}`)) {
            appendTerminalLine(`${command}: cancelled.`);
            return;
        }

        terminalRunning.current = true;
        setTerminalBusy(true);
        appendTerminalLine(`${command}: running...`);
        try {
            switch (command) {
                case 'migration': {
                    // const migration = await import("global/Transform/Sprint48Migration");
                    // await migration.RunBatch(dataSource);
                    break;
                }
                case 'remove-deprecated':
                    await debugRemoveDeprecatedFields();
                    break;
                case 'remove-duplicates':
                    await debugRemoveDuplicates();
                    break;
            }
            appendTerminalLine(`${command}: finished.${command === 'migration' ? ' Review the migration results for each snapshot.' : ' Current snapshot updated. Autosave is disabled.'}`);
        } catch (error) {
            appendTerminalLine(`${command}: failed. ${error instanceof Error ? error.message : String(error)}`);
            emitCustomEvent({ eventType: 'globalException', details: error });
        } finally {
            terminalRunning.current = false;
            setTerminalBusy(false);
        }
    };

    const submitTerminalCommand = async (input: string) => {
        const text = input.trim();
        if (!text)
            return;
        const [commandName, ...args] = text.split(/\s+/);
        const command = commandName.toLowerCase();
        appendTerminalLine(`> ${text}`);
        if (command === 'test') {
            if (!args.length) {
                appendTerminalLine('Usage: test <name>');
                return;
            }
            window.alert(`Hi ${args.join(' ')}!`);
        } else if (args.length && (command === 'help' || command === 'clear' || advancedCommands.some(action => action === command))) {
            appendTerminalLine(`${command} does not accept parameters.`);
        } else if (command === 'help') {
            appendTerminalLine('migration — Run Sprint 48 migration\nremove-deprecated — Remove deprecated fields\nremove-duplicates — Remove duplicate attribute sets\ntest <name> — Show a greeting (example: test John)\nhelp — Show commands\nclear — Clear terminal output');
        } else if (command === 'clear') {
            setTerminalLines([]);
        } else {
            const action = advancedCommands.find(action => action === command);
            if (action)
                await runAdvancedCommand(action);
            else
                appendTerminalLine(`Unknown command: ${command}. Type help to list commands.`);
        }
    };

    const onRenderAdvanced = (showNavigation = true) => {
        const isActive = viewModel.mode === IDebuggerState.advanced;
        const view = <button
            className={anchorClass}
            title="Open save settings and the command console for migrations and data maintenance."
            style={isActive ? { color: '#fff', background: '#263953' } : undefined}
            aria-expanded={isActive}
            aria-controls="debugger-advanced-options"
            onClick={() => { setViewModel(vm => ({ ...vm, mode: isActive ? IDebuggerState.unknown : IDebuggerState.advanced })) }}
        >
            <Icon className={actionIconClass} iconName="DeveloperTools" aria-hidden="true" />
            <span style={{ textTransform: 'uppercase' }}>Advanced</span>
            <Icon iconName={isActive ? 'ChevronDown' : 'ChevronRight'} styles={{ root: { marginLeft: 'auto', color: '#8fa2bd', fontSize: '12px' } }} aria-hidden="true" />
        </button>;

        if (!showNavigation)
            return view;

        if (!isActive)
            return <></>;

        return <div id="debugger-advanced-options" className={slideRightIn400}>
            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Control snapshot cloning and automatic saving.">Save behavior <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    <button className={anchorClass} title="Toggle cloning on save and disable autosave." onClick={
                        () => {
                            dispatch(updateDebug({ clone: !state.status.debug.clone }));
                            dispatch(updateDebug({ autoSaveOff: true }));
                        }} >
                        {getToggleIcon(state.status.debug.clone)} Clone on save
                    </button>

                    <button className={anchorClass} title="Toggle whether automatic saving is disabled. A filled green circle means autosave is disabled." onClick={
                        () => {
                            dispatch(updateDebug({ autoSaveOff: !state.status.debug.autoSaveOff }));
                        }} >
                        {getToggleIcon(state.status.debug.autoSaveOff)} Disable autosave
                    </button>
                </div>
            </details>
            <details className={submenuClass} open>
                <summary className={submenuSummaryClass} title="Run built-in commands. Type help to list commands or press Tab to autocomplete. Commands that modify data require confirmation.">Command console <Icon className={expandCollapseIconClass} iconName="ChevronDown" aria-hidden="true" /></summary>
                <div className={submenuContentClass}>
                    <p style={{ color: '#9aabc2', fontSize: '12px' }}>
                        Run built-in commands below. Type help to list commands. Press Tab to autocomplete or Shift+Tab to move focus out. This is not a remote shell. xterm.js loads from jsDelivr when opened.
                    </p>
                    <AdvancedTerminal
                        lines={terminalLines}
                        busy={terminalBusy}
                        commands={[...advancedCommands, 'test', 'help', 'clear']}
                        onCommand={command => {
                            if (!terminalRunning.current)
                                void submitTerminalCommand(command);
                        }}
                        onError={error => {
                            appendTerminalLine(`Unable to load the terminal: ${error.message}`);
                            emitCustomEvent({ eventType: 'globalException', details: error });
                        }}
                    />
                </div>
            </details>
        </div>;
    }

    const onRenderView = () => {
        return (
            <div className={terminalClass} style={{ overflowY: 'auto' }}>
                <header className={terminalHeaderClass}>
                    <div>
                        <div style={{ color: '#8da4c4', fontSize: '11px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' }}>BCBSSC · Internal tools</div>
                        <h2 style={{ margin: '5px 0 0', color: '#f3f6fb', fontSize: '24px', fontWeight: 600, textTransform: 'uppercase' }}>Management Terminal</h2>
                    </div>
                    <button className={anchorClass} title="Close the management console. You can also press Escape." style={{ width: 'auto', padding: '9px 12px' }} onClick={() => { setHidden(true) }}>
                        Close <span aria-hidden="true">×</span>
                    </button>
                </header>

                <div className={terminalBodyClass}>
                    <nav className={navigationClass} aria-label="Management console navigation">
                        <div style={{ padding: '8px 12px 10px', color: '#8193ad', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Sections</div>
                        {onRenderAdministrative(false)}
                        {onRenderDeveloper(false)}
                        {onRenderAdvanced(false)}
                    </nav>
                    <section className={contentClass} aria-label="Selected tools">
                        {viewModel.mode === IDebuggerState.unknown &&
                            <div style={{ padding: '24px 8px', color: '#9aabc2' }}>
                                <div style={{ marginBottom: '8px', color: '#e7edf7', fontSize: '18px', fontWeight: 600, textTransform: 'uppercase' }}>Select a section</div>
                                <div>Choose a section to view its tools.</div>
                            </div>
                        }
                        {viewModel.mode === IDebuggerState.administrative && onRenderAdministrative()}
                        {viewModel.mode === IDebuggerState.developer && onRenderDeveloper()}
                        {viewModel.mode === IDebuggerState.advanced && onRenderAdvanced()}
                    </section>
                </div>
            </div>
        );
    }

    return onRenderScreenWrap(onRenderView());
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(Debugger);